import { AlertTriangle } from 'lucide-react';

import type { PreflightResult } from '@/types/provision';

import type { WizardForm } from './step-connection';

interface StepReviewProps {
  form: WizardForm;
  preflight: PreflightResult | null;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
}

function SummaryRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="text-sm font-medium">{value || '—'}</dd>
    </>
  );
}

export function StepReview({ form, preflight, acknowledged, onAcknowledge }: StepReviewProps): JSX.Element {
  const requirementsUnmet =
    preflight === null || preflight.requirements === null || !preflight.requirements.meets;

  const location = [form.region_city, form.region_country, form.region_continent]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-4">
      {/* Two-column summary card */}
      <div className="rounded-md border p-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
          <SummaryRow
            label="Role"
            value={
              {
                vpn_server: 'VPN server',
                v2ray_edge: 'Reality edge',
                proxy_ingress: 'Proxy ingress',
                bootstrap_edge: 'Bootstrap edge',
              }[form.role]
            }
          />
          <SummaryRow label="Name" value={form.name} />
          <SummaryRow label="Provider" value={form.provider} />
          <SummaryRow label="Public IP" value={form.public_ip} />
          <SummaryRow label="VM type" value={form.vm_type} />
          <SummaryRow label="Location" value={location} />
          {form.role === 'vpn_server' ? (
            <SummaryRow label="Mode / protocol" value={`${form.connection_mode} / ${form.protocol}`} />
          ) : (
            <SummaryRow label="Transport" value="VLESS-Reality (generated on deploy)" />
          )}
        </dl>
      </div>

      {requirementsUnmet && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 shrink-0"
                checked={acknowledged}
                onChange={(e) => onAcknowledge(e.target.checked)}
              />
              <span className="text-sm text-amber-800 dark:text-amber-200">
                The host does not meet the recommended minimum requirements (or they could not be
                verified). I understand and want to launch anyway.
              </span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
